import { useState, type FormEvent, type ReactNode } from 'react';

import { apiAsset } from '@/config';
import type { Vehicle } from '@/types';
import {
  AIRPORTS,
  FLEET_BADGES,
  THEME_PREVIEW_SELECT,
  faqItems,
  sectionChoices,
  sectionFlag,
  sectionNumber,
  sectionText,
  type PublicTheme,
  type ThemeAgency,
  type ThemeSection,
} from '@/lib/storefront/theme-document';

export function ThemeHome({
  theme,
  vehicles,
  interactive = false,
  onSearch,
  onViewVehicle,
}: {
  theme: PublicTheme;
  vehicles: Vehicle[];
  interactive?: boolean;
  onSearch: () => void;
  onViewVehicle: (vehicle: Vehicle) => void;
}) {
  return (
    <div>
      {theme.sections.map((section) => (
        section.enabled ? (
          <ThemeSectionView
            key={section.id}
            section={section}
            theme={theme}
            vehicles={vehicles}
            interactive={interactive}
            onSearch={onSearch}
            onViewVehicle={onViewVehicle}
          />
        ) : null
      ))}
    </div>
  );
}

function ThemeSectionView({
  section,
  theme,
  vehicles,
  interactive,
  onSearch,
  onViewVehicle,
}: {
  section: ThemeSection;
  theme: PublicTheme;
  vehicles: Vehicle[];
  interactive: boolean;
  onSearch: () => void;
  onViewVehicle: (vehicle: Vehicle) => void;
}) {
  const settings = section.settings;
  const agency = theme.agency;
  const radius = theme.tokens.radius;
  const sticky = section.type === 'whatsapp_lead' && sectionText(settings, 'placement') === 'sticky';
  let body: ReactNode = null;
  switch (section.type) {
    case 'announcement_bar':
      body = <p className="bg-[var(--color-primary)] px-4 py-2 text-center text-sm font-semibold text-white">{sectionText(settings, 'message')}</p>;
      break;
    case 'hero_banner':
      body = <Hero settings={settings} radius={radius} onSearch={onSearch} />;
      break;
    case 'search_filter':
      body = <SearchFilter settings={settings} radius={radius} onSearch={onSearch} />;
      break;
    case 'featured_fleet':
      body = <Fleet settings={settings} vehicles={vehicles} radius={radius} onViewVehicle={onViewVehicle} />;
      break;
    case 'agency_showcase':
      body = <Showcase heading={sectionText(settings, 'heading')} agency={agency} radius={radius} />;
      break;
    case 'trust_proof':
      body = (
        <section className="mx-auto max-w-5xl px-4 py-10">
          <h2 className="text-2xl font-semibold">{sectionText(settings, 'heading')}</h2>
          <p className="mt-2 text-sm opacity-80">{sectionText(settings, 'notice')}</p>
        </section>
      );
      break;
    case 'crm_lead_capture':
      body = (
        <Lead
          agency={agency}
          radius={radius}
          showWhatsapp={sectionFlag(settings, 'showWhatsapp')}
          showForm={sectionFlag(settings, 'showForm')}
          buttonLabel={sectionText(settings, 'buttonLabel')}
          messageTemplate={sectionText(settings, 'messageTemplate')}
        />
      );
      break;
    case 'whatsapp_lead':
      body = (
        <WhatsAppLead
          agency={agency}
          radius={radius}
          label={sectionText(settings, 'buttonLabel')}
          message={sectionText(settings, 'messageTemplate')}
          phone={sectionText(settings, 'phone')}
        />
      );
      break;
    case 'faq_section':
      body = <Faq items={faqItems(settings)} />;
      break;
    case 'footer':
      body = <ThemeFooter theme={theme} />;
      break;
    default: {
      const unexpected: never = section.type;
      return unexpected;
    }
  }

  return (
    <SectionFrame id={section.id} interactive={interactive} sticky={sticky}>
      {body}
    </SectionFrame>
  );
}

function SectionFrame({
  id,
  interactive,
  sticky,
  children,
}: {
  id: string;
  interactive: boolean;
  sticky: boolean;
  children: ReactNode;
}) {
  return (
    <div
      data-theme-section={id}
      className={`group relative ${sticky ? 'sticky bottom-4 z-30' : ''} ${interactive ? 'outline outline-2 outline-transparent hover:outline-blue-500' : ''}`}
      onClickCapture={interactive ? (event) => {
        event.preventDefault();
        event.stopPropagation();
        window.parent.postMessage({ type: THEME_PREVIEW_SELECT, id }, '*');
      } : undefined}
    >
      {interactive && (
        <span className="pointer-events-none absolute right-2 top-2 z-20 hidden rounded-full bg-blue-600 px-2 py-1 text-xs font-semibold text-white group-hover:inline-flex">
          Edit
        </span>
      )}
      {children}
    </div>
  );
}

export function ThemeFooter({ theme }: { theme: PublicTheme }) {
  const footer = theme.sections.find((section) => section.type === 'footer' && section.enabled);
  if (!footer) return null;
  const agency = theme.agency;
  return (
    <footer className="mt-auto border-t border-black/10 px-4 py-8 text-sm">
      <p className="font-semibold">{agency.name}</p>
      <p className="opacity-80">{agency.city}{agency.address ? ` · ${agency.address}` : ''}</p>
      {sectionFlag(footer.settings, 'showLegal') && (
        <p className="mt-2 opacity-80">
          {[agency.ice && `ICE ${agency.ice}`, agency.rc && `RC ${agency.rc}`, agency.patente && `Patente ${agency.patente}`].filter(Boolean).join(' · ')}
        </p>
      )}
    </footer>
  );
}

function Hero({ settings, radius, onSearch }: { settings: ThemeSection['settings']; radius: string; onSearch: () => void }) {
  const layout = sectionText(settings, 'layout');
  const split = layout === 'split';
  const overlay = Math.min(80, Math.max(0, sectionNumber(settings, 'overlay', 0)));
  return (
    <section className="relative overflow-hidden text-white" style={heroStyle(settings)}>
      {overlay > 0 && <div className="absolute inset-0 bg-black" style={{ opacity: overlay / 100 }} />}
      <div className={`relative mx-auto grid max-w-5xl gap-6 px-4 py-16 ${split ? 'md:grid-cols-2' : ''}`}>
        <div>
          <h1 className="text-4xl font-semibold tracking-tight">{sectionText(settings, 'tagline')}</h1>
          {sectionText(settings, 'subheadline') !== '' && <p className="mt-3 text-sm opacity-90">{sectionText(settings, 'subheadline')}</p>}
          {layout === 'search' ? (
            <form className="mt-6 flex flex-wrap gap-2" onSubmit={(event) => { event.preventDefault(); onSearch(); }}>
              <button type="submit" className={`${radius} bg-white px-4 py-2 text-sm font-semibold text-[var(--color-primary)]`}>Search cars</button>
            </form>
          ) : (
            <button type="button" onClick={onSearch} className={`${radius} mt-6 bg-white px-4 py-2 text-sm font-semibold text-[var(--color-primary)]`}>See the fleet</button>
          )}
        </div>
        {layout === 'slider' && <div className={`${radius} min-h-40 bg-white/20`} />}
      </div>
    </section>
  );
}

function heroStyle(settings: ThemeSection['settings']): { backgroundColor?: string; backgroundImage?: string; backgroundSize?: string; backgroundPosition?: string } {
  const background = sectionText(settings, 'background');
  const imageUrl = sectionText(settings, 'imageUrl');
  if (background === 'image' && imageUrl !== '') {
    return { backgroundImage: `url(${apiAsset(imageUrl)})`, backgroundSize: 'cover', backgroundPosition: 'center' };
  }
  if (background === 'gradient') {
    return { backgroundImage: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))' };
  }
  return { backgroundColor: 'var(--color-primary)' };
}

function SearchFilter({ settings, radius, onSearch }: { settings: ThemeSection['settings']; radius: string; onSearch: () => void }) {
  const badges = sectionChoices(settings, 'badges', FLEET_BADGES);
  const airports = sectionChoices(settings, 'airports', AIRPORTS);
  return (
    <section className="mx-auto max-w-5xl space-y-3 px-4 py-6">
      <div className="flex flex-wrap gap-2">
        {badges.map((badge) => <span key={badge} className={`${radius} border border-black/15 px-3 py-1 text-sm`}>{badge}</span>)}
      </div>
      <form className="flex flex-wrap items-center gap-2" onSubmit={(event) => { event.preventDefault(); onSearch(); }}>
        {airports.map((airport) => <span key={airport} className={`${radius} bg-black/5 px-3 py-2 text-sm`}>{airport}</span>)}
        <button type="submit" className={`${radius} bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white`}>Search cars</button>
      </form>
    </section>
  );
}

function Fleet({
  settings,
  vehicles,
  radius,
  onViewVehicle,
}: {
  settings: ThemeSection['settings'];
  vehicles: Vehicle[];
  radius: string;
  onViewVehicle: (vehicle: Vehicle) => void;
}) {
  const view = sectionText(settings, 'view');
  const limit = Math.min(12, Math.max(1, sectionNumber(settings, 'limit', 8)));
  const badges = sectionChoices(settings, 'badges', FLEET_BADGES);
  const listed = vehicles.slice(0, limit);
  return (
    <section className="mx-auto max-w-5xl px-4 py-8">
      <h2 className="text-2xl font-semibold">Fleet</h2>
      <div className="mt-3 flex flex-wrap gap-2">
        {badges.map((badge) => <span key={badge} className={`${radius} border border-black/10 px-2 py-1 text-xs`}>{badge}</span>)}
      </div>
      <ul className={view === 'carousel' ? 'mt-4 flex gap-3 overflow-x-auto' : 'mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3'}>
        {listed.map((vehicle) => (
          <li key={vehicle.id} className={`${radius} min-w-56 border border-black/10 p-3`}>
            <p className="font-medium">{vehicle.name}</p>
            <p className="text-sm opacity-70">{vehicle.category}</p>
            <button type="button" onClick={() => onViewVehicle(vehicle)} className="mt-2 text-sm font-semibold text-[var(--color-primary)]">Details</button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Showcase({ heading, agency, radius }: { heading: string; agency: ThemeAgency; radius: string }) {
  const query = encodeURIComponent(agency.city || agency.address || 'Morocco');
  return (
    <section className="mx-auto grid max-w-5xl gap-4 px-4 py-8 md:grid-cols-2">
      <div>
        <h2 className="text-2xl font-semibold">{heading}</h2>
        <p className="mt-2 text-sm">{agency.city}</p>
        <p className="text-sm opacity-80">{agency.address}</p>
        <p className="text-sm opacity-80">{agency.phone}</p>
        <ul className="mt-3 space-y-1 text-sm">
          {agency.hours.map((row) => (
            <li key={`${row.label}-${row.value}`}>{row.label}: {row.value}</li>
          ))}
        </ul>
      </div>
      <iframe title="Pickup map" src={`https://maps.google.com/maps?q=${query}&z=12&output=embed`} className={`${radius} h-56 w-full border-0`} />
    </section>
  );
}

function Lead({
  agency,
  radius,
  showWhatsapp,
  showForm,
  buttonLabel,
  messageTemplate,
}: {
  agency: ThemeAgency;
  radius: string;
  showWhatsapp: boolean;
  showForm: boolean;
  buttonLabel: string;
  messageTemplate: string;
}) {
  const [note, setNote] = useState(messageTemplate);
  const [template, setTemplate] = useState(messageTemplate);
  if (template !== messageTemplate) {
    setTemplate(messageTemplate);
    setNote(messageTemplate);
  }
  const digits = agency.whatsapp.replace(/[^\d]/g, '');

  function send(event: FormEvent) {
    event.preventDefault();
    if (digits === '') return;
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(note)}`, '_blank', 'noopener,noreferrer');
  }

  return (
    <section className="mx-auto max-w-5xl px-4 py-8">
      <h2 className="text-2xl font-semibold">Reserve</h2>
      <div className="mt-4 flex flex-wrap gap-3">
        {showWhatsapp && digits !== '' && (
          <a className={`${radius} bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white`} href={`https://wa.me/${digits}?text=${encodeURIComponent(messageTemplate)}`} target="_blank" rel="noreferrer">
            {buttonLabel || 'WhatsApp'}
          </a>
        )}
        {showForm && (
          <form onSubmit={send} className="flex flex-wrap gap-2">
            <input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Airport delivery" className={`${radius} border border-black/15 px-3 py-2 text-sm`} />
            <button type="submit" className={`${radius} border border-black/15 px-3 py-2 text-sm font-semibold`}>{buttonLabel || 'Send'}</button>
          </form>
        )}
      </div>
    </section>
  );
}

function WhatsAppLead({
  agency,
  radius,
  label,
  message,
  phone,
}: {
  agency: ThemeAgency;
  radius: string;
  label: string;
  message: string;
  phone: string;
}) {
  const digits = (phone !== '' ? phone : agency.whatsapp).replace(/[^\d]/g, '');
  return (
    <section className="mx-auto max-w-5xl px-4 py-4">
      {digits === '' ? (
        <p className="text-sm opacity-80">Add a WhatsApp number to open a chat.</p>
      ) : (
        <a
          className={`${radius} inline-flex bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white`}
          href={`https://wa.me/${digits}?text=${encodeURIComponent(message)}`}
          target="_blank"
          rel="noreferrer"
        >
          {label || 'WhatsApp'}
        </a>
      )}
    </section>
  );
}

function Faq({ items }: { items: { question: string; answer: string }[] }) {
  return (
    <section className="mx-auto max-w-5xl px-4 py-8">
      <h2 className="text-2xl font-semibold">Rental policies</h2>
      <div className="mt-4 space-y-2">
        {items.map((item, index) => (
          <details key={`${index}-${item.question}`} className="border-b border-black/10 py-2">
            <summary className="cursor-pointer font-medium">{item.question}</summary>
            <p className="mt-2 text-sm opacity-80">{item.answer}</p>
          </details>
        ))}
      </div>
    </section>
  );
}
